import React from 'react';
import { Checkbox } from 'antd';
import SketchOptions from '../SketchOptions';
import { Sketch } from '../../../../models/sketch.model';
import { useLegendForm, useLegendUI } from '../../../../context/MapLegendContext';
import { useSketchProperitesContext } from '../../../../context/SketchPropertiesContext';
import * as Cesium from 'cesium';
import { applyEntityStyles, getPropsObject } from '../../../../shared/utils/sketchHelpers';
import './SketchList.scss';

interface SketchListProps {
  sketches: Sketch[];
  onToggleVisible: (sketch: Sketch, checked: boolean) => void;
  onDeleteSketch: (id: string) => void;
  onAddSketch: () => void;
  onViewSketchProperties: (sketch: Sketch) => void;
  viewerRef: any;
  pointsRef: any;
}

const SketchList: React.FC<SketchListProps> = ({
  sketches,
  onToggleVisible,
  onDeleteSketch,
  onAddSketch,
  onViewSketchProperties,
  viewerRef,
  pointsRef,
}) => {
  const { setSelectedSketch, setSelectedSketchItem, setSketchDrawerState } = useLegendForm();
  const { sketchDrawerVisible, setSketchDrawerVisible } = useLegendUI();
  const { setState, resetState } = useSketchProperitesContext();

  const handleSketchClick = (sketch: Sketch) => {
    setSelectedSketch(sketch);
    setSelectedSketchItem(null);
    pointsRef.current = [];
    viewerRef.current?.entities.removeAll();
    setSketchDrawerState('view');

    if ((sketch as any).config) {
      setState((sketch as any).config);
    } else {
      resetState?.();
    }
    if (!sketchDrawerVisible) setSketchDrawerVisible(true);
    handleZoomToSketch(sketch);
  };

  const handleZoomToSketch = (sketch: Sketch) => {
    const viewer = viewerRef.current;
    viewer?.dataSources.removeAll();
    viewer?.entities.removeAll();
    setSketchDrawerVisible(false);
    setSelectedSketch(null);
    setSelectedSketchItem(null);

    for (const item of sketch.items) {
      const geojson = typeof item.geojson === 'string' ? JSON.parse(item.geojson) : item.geojson;
      Cesium.GeoJsonDataSource.load(geojson, { clampToGround: false }).then((dataSource) => {
        viewer.dataSources.add(dataSource);
        const entities = dataSource.entities.values;
        for (const entity of entities) {
          (entity as any).__sketchItemId = item.id;
          (entity as any).__sketchItemType = item.type;
          const rawProps = getPropsObject(entity.properties);
          let style = rawProps.style;
          if (typeof style === 'string')
            try {
              style = JSON.parse(style);
            } catch {}
          applyEntityStyles(entity, style || {}, rawProps);
        }
        viewer.zoomTo(dataSource);
      });
    }
  };

  return (
    <div className="sketch-list">
      {sketches.map((sketch, index) => (
        <div key={index} className="legend-layer">
          <div className="legend-row legend-row--sketch">
            <Checkbox checked={sketch.visible} onChange={(e) => onToggleVisible(sketch, e.target.checked)} />
            <span className="legend-row__title" title={sketch.title} onClick={() => handleSketchClick(sketch)}>
              {sketch.title}
            </span>
            <SketchOptions
              item={sketch}
              onZoomToSketch={() => handleZoomToSketch(sketch)}
              onSketchProperties={() => onViewSketchProperties(sketch)}
              onDelete={() => onDeleteSketch(sketch.id!)}
            />
          </div>
        </div>
      ))}
      <div className="sketch-list__add">
        <button className="add-sketch-btn" onClick={onAddSketch}>
          <img src="/assets/pencil-icon.svg" alt="" className="add-sketch-btn__icon" />
          Create a New Sketch
        </button>
      </div>
    </div>
  );
};

export default SketchList;
